import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { c as cl } from './clsx.m.js';
import { c as createTypeValidator, a as createLogger, A as updateColorElementStyle, u as updateElementStyle, d as isNumber, f as isUnset, n as numToRem } from './utils.js';
import { d as defineCustomElement$1 } from './cl-typography2.js';

// Constants
const component = 'cl-divider';
const dividerConstants = {
  ALIGNMENT: {
    START: 'start',
    MIDDLE: 'middle',
    END: 'end',
  },
  THICKNESS: {
    EXTRA_SMALL: 'extra-small',
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
  },
  VARIANT: {
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
    TERTIARY: 'tertiary',
  },
};
// Type-validators
const dividerValidator = {
  align: createTypeValidator(dividerConstants.ALIGNMENT),
  thickness: createTypeValidator(dividerConstants.THICKNESS),
  variant: createTypeValidator(dividerConstants.VARIANT),
};

const clDividerCss = ".sc-cl-divider-h{--divider-spacing-fallback:var(--spacing-3);--divider-label-padding:var(--spacing-1);--first-bar-size:100%;--last-bar-size:100%}.divider.sc-cl-divider-h{display:block}.divider.sc-cl-divider-h .divider__bars.sc-cl-divider{align-items:center;display:flex;height:100%;justify-content:center;width:100%}.divider.sc-cl-divider-h .divider__bars.sc-cl-divider::before,.divider.sc-cl-divider-h .divider__bars.sc-cl-divider::after{background:var(--custom-divider-color, var(--divider-color, rgb(var(--color-neutral-300))));content:\"\";position:relative;vertical-align:middle}.divider.sc-cl-divider-h .divider__bars.sc-cl-divider cl-typography.sc-cl-divider{--typography-color:var(--custom-divider-color, var(--divider-color, rgb(var(--color-neutral-300))))}.divider--extra-small.sc-cl-divider-h{--divider-thickness:calc(var(--spacing-eighth) / 2)}.divider--small.sc-cl-divider-h{--divider-thickness:var(--spacing-eighth)}.divider--medium.sc-cl-divider-h{--divider-thickness:var(--spacing-quarter)}.divider--large.sc-cl-divider-h{--divider-thickness:var(--spacing-half)}.sc-cl-divider-h:not(.divider--vertical).divider{height:-moz-max-content;height:max-content;width:100%}.sc-cl-divider-h:not(.divider--vertical) .divider__bars.sc-cl-divider::before,.sc-cl-divider-h:not(.divider--vertical) .divider__bars.sc-cl-divider::after{height:var(--divider-thickness);margin:var(--custom-divider-spacing, var(--divider-spacing, var(--divider-spacing-fallback))) 0}.sc-cl-divider-h:not(.divider--vertical) .divider__bars.sc-cl-divider::before{border-radius:var(--border-radius-small) 0 0 var(--border-radius-small);width:var(--first-bar-size)}.sc-cl-divider-h:not(.divider--vertical) .divider__bars.sc-cl-divider::after{border-radius:0 var(--border-radius-small) var(--border-radius-small) 0;width:var(--last-bar-size)}.sc-cl-divider-h:not(.divider--vertical) .divider__bars.sc-cl-divider cl-typography.sc-cl-divider{margin:0 var(--divider-label-padding)}.divider--vertical.sc-cl-divider-h{--divider-spacing-fallback:0}.divider--vertical.divider.sc-cl-divider-h{height:100%;width:-moz-max-content;width:max-content}.divider--vertical.sc-cl-divider-h .divider__bars.sc-cl-divider{flex-direction:column}.divider--vertical.sc-cl-divider-h .divider__bars.sc-cl-divider::before,.divider--vertical.sc-cl-divider-h .divider__bars.sc-cl-divider::after{width:var(--divider-thickness)}.divider--vertical.sc-cl-divider-h .divider__bars.sc-cl-divider::before{border-radius:var(--border-radius-small) var(--border-radius-small) 0 0;height:var(--first-bar-size);margin-top:var(--custom-divider-spacing, var(--divider-spacing, var(--divider-spacing-fallback)))}.divider--vertical.sc-cl-divider-h .divider__bars.sc-cl-divider::after{border-radius:0 0 var(--border-radius-small) var(--border-radius-small);height:var(--last-bar-size);margin-bottom:var(--custom-divider-spacing, var(--divider-spacing, var(--divider-spacing-fallback)))}.divider--vertical.sc-cl-divider-h .divider__bars.sc-cl-divider cl-typography.sc-cl-divider{margin:var(--divider-label-padding) 0}.divider--label.sc-cl-divider-h{--divider-spacing-fallback:var(--spacing-1)}.divider--label.divider--label.sc-cl-divider-h cl-typography.sc-cl-divider{min-height:calc(var(--font-size-microcopy) + var(--size-half));min-width:-moz-fit-content;min-width:fit-content}.divider--label.divider--label.sc-cl-divider-h:not(.divider--vertical) cl-typography.sc-cl-divider{margin:0 var(--divider-label-padding)}.divider--label.divider--label.divider--vertical.sc-cl-divider-h cl-typography.sc-cl-divider{margin:var(--divider-label-padding) 0}.divider--label.divider--label.sc-cl-divider-h .divider__bars.sc-cl-divider::before,.divider--label.divider--label.sc-cl-divider-h .divider__bars.sc-cl-divider::after{border-radius:var(--border-radius-interactive-small)}.divider--label.divider--primary.divider--start.sc-cl-divider-h{--first-bar-size:10%;--last-bar-size:90%}.divider--label.divider--primary.divider--middle.sc-cl-divider-h{--first-bar-size:100%;--last-bar-size:100%}.divider--label.divider--primary.divider--end.sc-cl-divider-h{--first-bar-size:90%;--last-bar-size:10%}.divider--label.divider--secondary.sc-cl-divider-h{--first-bar-size:var(--size-2);--last-bar-size:var(--size-2)}.divider--label.divider--tertiary.sc-cl-divider-h{--first-bar-size:0;--last-bar-size:0}.divider--label.divider--secondary.divider--start.sc-cl-divider-h>.divider__bars.sc-cl-divider,.divider--label.divider--tertiary.divider--start.sc-cl-divider-h>.divider__bars.sc-cl-divider{justify-content:flex-start}.divider--label.divider--secondary.divider--middle.sc-cl-divider-h>.divider__bars.sc-cl-divider,.divider--label.divider--tertiary.divider--middle.sc-cl-divider-h>.divider__bars.sc-cl-divider{justify-content:center}.divider--label.divider--secondary.divider--end.sc-cl-divider-h>.divider__bars.sc-cl-divider,.divider--label.divider--tertiary.divider--end.sc-cl-divider-h>.divider__bars.sc-cl-divider{justify-content:flex-end}.divider--label.divider.sc-cl-divider-h:not(.divider--vertical).divider--primary .divider__bars.sc-cl-divider::before,.divider--label.divider.sc-cl-divider-h:not(.divider--vertical).divider--primary .divider__bars.sc-cl-divider::after{min-width:15%}.divider--label.divider.sc-cl-divider-h:not(.divider--vertical).divider--secondary .divider__bars.sc-cl-divider::before,.divider--label.divider.sc-cl-divider-h:not(.divider--vertical).divider--secondary .divider__bars.sc-cl-divider::after{min-width:var(--size-2)}";

const ClDivider = /*@__PURE__*/ proxyCustomElement(class ClDivider extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    this.align = 'middle';
    this.color = '';
    this.label = '';
    this.spacing = undefined;
    this.thickness = 'small';
    this.variant = 'primary';
    this.vertical = false;
  }
  /**
   * Validates the align property
   */
  handleAlign(align) {
    if (!dividerValidator.align(align)) {
      this.logger.error.attribute.align(`Given invalid value "${align}".`);
    }
  }
  /**
   * Validates the color property and updates the styles accordingly
   */
  handleColor(color) {
    updateColorElementStyle(this.element, color, '--custom-divider-color', this.logger.error.attribute.color);
  }
  /**
   * Validates the spacing property and updates the styles accordingly
   */
  handleSpacing(spacing) {
    const cssVariable = '--custom-divider-spacing';
    switch (true) {
      case isUnset(spacing):
        updateElementStyle(this.element, undefined, cssVariable);
        break;
      case !isNumber(spacing):
        this.logger.error.attribute.spacing(`Given invalid value "${spacing}".`);
        break;
      default:
        updateElementStyle(this.element, numToRem(spacing), cssVariable);
        break;
    }
  }
  /**
   * Validates the thickness property
   */
  handleThickness(thickness) {
    if (!dividerValidator.thickness(thickness)) {
      this.logger.error.attribute.thickness(`Given invalid value "${thickness}".`);
    }
  }
  /**
   * Validates the variant property
   */
  handleVariant(variant) {
    if (!dividerValidator.variant(variant)) {
      this.logger.error.attribute.variant(`Given invalid value "${variant}".`);
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Validate the color property;
   * - Validate the spacing property;
   * - Validate the align property;
   * - Validate the thickness property;
   * - Validate the variant property;
   */
  connectedCallback() {
    this.handleColor(this.color);
    this.handleSpacing(this.spacing);
    this.handleAlign(this.align);
    this.handleThickness(this.thickness);
    this.handleVariant(this.variant);
  }
  render() {
    const { align, label, thickness, variant, vertical } = this;
    return (h(Host, { class: cl('divider', `divider--${thickness}`, {
        [`divider--label divider--${variant} divider--${align}`]: label,
        'divider--vertical': vertical,
      }) }, h("span", { class: 'divider__bars' }, label && (h("cl-typography", { class: 'divider__label', variant: 'microcopy' }, label)))));
  }
  get element() { return this; }
  static get watchers() { return {
    "align": ["handleAlign"],
    "color": ["handleColor"],
    "spacing": ["handleSpacing"],
    "thickness": ["handleThickness"],
    "variant": ["handleVariant"]
  }; }
  static get style() { return clDividerCss; }
}, [2, "cl-divider", {
    "align": [1],
    "color": [1],
    "label": [1],
    "spacing": [2],
    "thickness": [1],
    "variant": [513],
    "vertical": [516]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-divider", "cl-typography"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-divider":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClDivider);
      }
      break;
    case "cl-typography":
      if (!customElements.get(tagName)) {
        defineCustomElement$1();
      }
      break;
  } });
}

export { ClDivider as C, defineCustomElement as d };

//# sourceMappingURL=cl-divider2.js.map